import React, { useMemo, useCallback, memo, useEffect } from 'react'
import { useInstantSearch } from 'react-instantsearch'
import { SearchTabBar } from './SearchTabBar'
import { CategorySection } from './CategorySection'
import { PlayerResultItem } from './PlayerResultItem'
import { NewsResultItem } from './NewsResultItem'
import { TeamResultItem } from './TeamResultItem'
import { ShopResultItem } from './ShopResultItem'
import type {
  PLLHit,
  ShopifyHit,
  SearchTab,
  ResultCounts,
  CategoryResults,
} from '../types'
import { SEARCH_TABS } from '../types'
import { EmptyState, ShopGridContainer } from '../styles'
import {
  addUniqueItem,
  parseSearchResults,
  calculateResultCounts,
  getTotalResultCount,
  handlePLLResultSelect,
  handleShopResultSelect,
  mergeAndSortByRelevancy,
} from '../../../Utils/search/search.utils'
import { trackSearchView } from '../../../Utils/search/searchInsights.utils'

type SearchResultsProps = {
  query: string
  activeTab: SearchTab
  onTabChange: (tab: SearchTab) => void
  onClose: () => void
}

const MAX_ITEMS_PER_SECTION = 5

type CategoryListProps = {
  items: PLLHit[]
  query: string
  onSelect: (item: PLLHit) => void
  ItemComponent: typeof PlayerResultItem | typeof NewsResultItem | typeof TeamResultItem
  limit?: number
}

const CategoryList = memo<CategoryListProps>(
  ({ items, query, onSelect, ItemComponent, limit }) => {
    const displayItems = limit ? items.slice(0, limit) : items
    return (
      <>
        {displayItems.map((item) => (
          <ItemComponent
            key={item.objectID}
            item={item}
            query={query}
            onSelect={() => onSelect(item)}
          />
        ))}
      </>
    )
  }
)
CategoryList.displayName = 'CategoryList'

type ShopListProps = {
  items: ShopifyHit[]
  query: string
  onSelect: (item: ShopifyHit) => void
  isGridView?: boolean
  limit?: number
}

const ShopList = memo<ShopListProps>(
  ({ items, query, onSelect, isGridView = false, limit }) => {
    const displayItems = limit ? items.slice(0, limit) : items
    return (
      <>
        {displayItems.map((item) => (
          <ShopResultItem
            key={item.objectID}
            item={item}
            query={query}
            onSelect={() => onSelect(item)}
            isGridView={isGridView}
          />
        ))}
      </>
    )
  }
)
ShopList.displayName = 'ShopList'

type AllResultsTabProps = {
  results: CategoryResults
  counts: ResultCounts
  query: string
  onTabChange: (tab: SearchTab) => void
  onSelectPLL: (item: PLLHit) => void
  onSelectShop: (item: ShopifyHit) => void
}

const AllResultsTab = memo<AllResultsTabProps>(
  ({ results, counts, query, onTabChange, onSelectPLL, onSelectShop }) => {
    const sortedResults = useMemo(() => mergeAndSortByRelevancy(results), [results])

    const leagueItems = useMemo(() => {
      const league: PLLHit[] = []
      const seenIds = new Set<string>()
      for (const { item, category } of sortedResults) {
        if (category !== 'shop') {
          addUniqueItem(league, seenIds, item as PLLHit)
          if (league.length >= MAX_ITEMS_PER_SECTION) break
        }
      }
      return league
    }, [sortedResults])

    const shopItems = useMemo(() => {
      const shop: ShopifyHit[] = []
      const seenIds = new Set<string>()
      for (const { item, category } of sortedResults) {
        if (category === 'shop') {
          addUniqueItem(shop, seenIds, item as ShopifyHit)
          if (shop.length >= MAX_ITEMS_PER_SECTION) break
        }
      }
      return shop
    }, [sortedResults])

    const leagueCount = counts.players + counts.teams + counts.news + counts.general
    const shopCount = counts.shop

    const getItemComponent = (item: PLLHit) => {
      if (item.type === 'player') return PlayerResultItem
      if (item.type === 'team') return TeamResultItem
      return NewsResultItem
    }

    return (
      <>
        {leagueItems.length > 0 && (
          <CategorySection
            title="League"
            totalCount={leagueCount}
            visibleCount={leagueItems.length}
            targetTab={SEARCH_TABS.ALL}
            onViewAll={() => { }}
          >
            {leagueItems.map((item) => {
              const ItemComponent = getItemComponent(item)
              return (
                <ItemComponent
                  key={item.objectID}
                  item={item}
                  query={query}
                  onSelect={() => onSelectPLL(item)}
                />
              )
            })}
          </CategorySection>
        )}

        {shopItems.length > 0 && (
          <CategorySection
            title="Shop"
            totalCount={shopCount}
            visibleCount={shopItems.length}
            targetTab={SEARCH_TABS.SHOP}
            onViewAll={onTabChange}
          >
            <ShopList
              items={shopItems}
              query={query}
              onSelect={onSelectShop}
              limit={MAX_ITEMS_PER_SECTION}
            />
          </CategorySection>
        )}
      </>
    )
  }
)
AllResultsTab.displayName = 'AllResultsTab'

export const SearchResults: React.FC<SearchResultsProps> = ({
  query,
  activeTab,
  onTabChange,
  onClose,
}) => {
  const { scopedResults } = useInstantSearch()

  const results = useMemo(() => parseSearchResults(scopedResults), [scopedResults])
  const counts = useMemo(() => calculateResultCounts(results), [results])

  useEffect(() => {
    if (!query || scopedResults.length === 0) return

    scopedResults.forEach((scopedResult) => {
      const searchResult = scopedResult.results
      if (!searchResult?.hits || searchResult.hits.length === 0) return

      const indexName = scopedResult.indexId
      const queryID = searchResult.queryID
      const objectIDs = searchResult.hits.map((hit) => hit.objectID)

      if (objectIDs.length > 0) {
        trackSearchView(indexName, objectIDs, queryID)
      }
    })
  }, [query, scopedResults])

  const sortedNews = useMemo(() => {
    return [...results.news].sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0
      const dateB = b.date ? new Date(b.date).getTime() : 0
      return dateB - dateA
    })
  }, [results.news])

  const handleSelectPLL = useCallback(
    (item: PLLHit) => handlePLLResultSelect(item, onClose, scopedResults),
    [onClose, scopedResults]
  )

  const handleSelectShop = useCallback(
    (item: ShopifyHit) => handleShopResultSelect(item, onClose, scopedResults),
    [onClose, scopedResults]
  )

  const totalResults = getTotalResultCount(counts)

  if (totalResults === 0) {
    return (
      <>
        <SearchTabBar activeTab={activeTab} onTabChange={onTabChange} counts={counts} />
        <EmptyState>No results found for &ldquo;{query}&rdquo;</EmptyState>
      </>
    )
  }

  return (
    <>
      <SearchTabBar activeTab={activeTab} onTabChange={onTabChange} counts={counts} />

      {activeTab === SEARCH_TABS.ALL && (
        <AllResultsTab
          results={results}
          counts={counts}
          query={query}
          onTabChange={onTabChange}
          onSelectPLL={handleSelectPLL}
          onSelectShop={handleSelectShop}
        />
      )}

      {activeTab === SEARCH_TABS.PLAYERS && results.players.length > 0 && (
        <CategorySection
          title="Players"
          totalCount={counts.players}
          visibleCount={counts.players}
          targetTab={SEARCH_TABS.PLAYERS}
          onViewAll={onTabChange}
        >
          <CategoryList
            items={results.players}
            query={query}
            onSelect={handleSelectPLL}
            ItemComponent={PlayerResultItem}
          />
        </CategorySection>
      )}

      {activeTab === SEARCH_TABS.NEWS && results.news.length > 0 && (
        <CategorySection
          title="News"
          totalCount={counts.news}
          visibleCount={counts.news}
          targetTab={SEARCH_TABS.NEWS}
          onViewAll={onTabChange}
        >
          <CategoryList
            items={sortedNews}
            query={query}
            onSelect={handleSelectPLL}
            ItemComponent={NewsResultItem}
          />
        </CategorySection>
      )}

      {activeTab === SEARCH_TABS.SHOP && results.shop.length > 0 && (
        <ShopGridContainer>
          <ShopList
            items={results.shop}
            query={query}
            onSelect={handleSelectShop}
            isGridView
          />
        </ShopGridContainer>
      )}

      {activeTab === SEARCH_TABS.TEAMS && results.teams.length > 0 && (
        <CategorySection
          title="Teams"
          totalCount={counts.teams}
          visibleCount={counts.teams}
          targetTab={SEARCH_TABS.TEAMS}
          onViewAll={onTabChange}
        >
          <CategoryList
            items={results.teams}
            query={query}
            onSelect={handleSelectPLL}
            ItemComponent={TeamResultItem}
          />
        </CategorySection>
      )}
    </>
  )
}
